/**
 * Playing Card Component
 * Renders a card face by compositing the real gem-art assets: a parchment
 * frame per jewel (border + jewel name baked in), a faceted gem-cut suit
 * icon sized per-suit so all 4 read as the same visual size (see ICON_WIDTH
 * — normalized by actual ink-pixel coverage, not bounding-box area: a
 * diamond only fills ~50% of its own bbox by geometry, so equal-bbox-area
 * sizing still looked visibly smaller than a heart, which fills ~68%), and
 * a flat silver (#E0E0E0) bold-serif rank glyph layered on top, centered on
 * the icon (same center point as the icon itself, both axes — an earlier
 * "bulb-centered" adjustment for clubs/spades' stems made the rank sit
 * visibly too high and was removed; simple centering is correct).
 * This app's card design deliberately leaves the corners empty
 * (poker-app-spec.md Section 4.5: "the four card corners, currently unused
 * in this app's card design"). A special hole card only gently pulses —
 * the special itself is shown as its own extra card (SpecialCard.tsx).
 */

import React from 'react';
import type { Card as SharedCard, ViewerFacingCard } from '@pokercubed/shared';
import { Jewel, Suit, Rank } from '@pokercubed/shared';
import { CARD_BACK_ART, FRAME_ART, ICON_ART, RANK_ART } from '../../assets/cards';

/**
 * Icon width as a fraction of card width, per suit — normalized so all 4
 * render the same actual ink-pixel area, not just the same bounding-box
 * area (a diamond's bbox is ~50% empty corner by geometry; a heart's is
 * ~32% empty; matching bbox area alone left diamonds looking smaller).
 * Anchored to spades at 55% width. Measured off the actual icon pixels
 * (alpha-channel coverage), not eyeballed.
 */
const ICON_WIDTH: Record<Suit, number> = {
  [Suit.HEARTS]: 0.562,
  [Suit.CLUBS]: 0.555,
  [Suit.DIAMONDS]: 0.627,
  [Suit.SPADES]: 0.55,
};

type SuitLetter = 'S' | 'H' | 'D' | 'C';

const LETTER_TO_SUIT: Record<SuitLetter, Suit> = {
  H: Suit.HEARTS,
  D: Suit.DIAMONDS,
  C: Suit.CLUBS,
  S: Suit.SPADES,
};

/** Plain suit symbols, for a Buried card whose jewel (and so its gem-coloured icon art) is hidden. */
const SUIT_SYMBOL: Record<Suit, string> = {
  [Suit.HEARTS]: '♥',
  [Suit.DIAMONDS]: '♦',
  [Suit.CLUBS]: '♣',
  [Suit.SPADES]: '♠',
};

const JEWEL_GLOW: Record<Jewel, string> = {
  [Jewel.RUBY]: '#e0466c',
  [Jewel.EMERALD]: '#3ec488',
  [Jewel.TOPAZ]: '#e0b84b',
  [Jewel.ONYX]: '#9b9bab',
};

/** Anything this component can be handed for `card`. */
type CardInput = SharedCard | ViewerFacingCard | string | null | undefined;

const isMasked = (card: CardInput): card is { masked: true } =>
  !!card && typeof card === 'object' && 'masked' in card;

const isHiddenCard = (card: CardInput): card is { hidden: true; special?: SharedCard['special'] } =>
  !!card && typeof card === 'object' && 'hidden' in card;

const isStructuredCard = (card: CardInput): card is SharedCard =>
  !!card && typeof card === 'object' && 'rank' in card && 'suit' in card;

const parseNotation = (card: string): { rank: Rank; suit: Suit } => {
  const letter = card.slice(-1).toUpperCase() as SuitLetter;
  const rank = card.slice(0, -1) as Rank;
  return { rank, suit: LETTER_TO_SUIT[letter] };
};

interface PlayingCardProps {
  /**
   * A full Card, a ViewerFacingCard (own hole card, an opponent's masked
   * `{ masked: true }`/hidden `{ hidden: true }` card), a legacy 'AS'
   * notation string, or null/undefined for an empty slot.
   */
  card?: CardInput;
  faceDown?: boolean;
  highlighted?: boolean;
  className?: string;
}

const PlayingCard: React.FC<PlayingCardProps> = ({
  card,
  faceDown = false,
  highlighted = false,
  className = '',
}) => {
  const ringClass = highlighted
    ? 'ring-2 ring-yellow-400 shadow-lg shadow-yellow-400/50'
    : 'ring-1 ring-black/30';

  // Oracle's masked view (spec 4.5): something is here and it's special, identity unknown.
  if (isMasked(card)) {
    return (
      <div
        className={`relative aspect-[5/7] rounded-lg overflow-hidden ${ringClass} bg-gradient-to-br from-purple-900 to-indigo-950 flex items-center justify-center animate-pulse ${className}`}
      >
        <div className="text-white/70 text-3xl font-bold">?</div>
      </div>
    );
  }

  if (faceDown || !card || isHiddenCard(card)) {
    // The back of every face-down card: the PokerCubed logo.
    const back = (
      <div
        data-testid="card-back"
        className={`relative aspect-[5/7] rounded-lg overflow-hidden ${ringClass} bg-white ${className}`}
      >
        <img
          src={CARD_BACK_ART}
          alt="P³"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          draggable={false}
        />
      </div>
    );
    // Seen through Spy: still face-down, but it pulses like any card a
    // special is on (the special itself shows beside the hole cards).
    if (!faceDown && isHiddenCard(card) && card.special) {
      return (
        <div className="relative">
          <div
            data-testid="special-holder-pulse"
            className="special-pulse absolute -inset-[3px] z-10 rounded-[10px] ring-2 ring-amber-200 shadow-[0_0_12px_3px_rgba(253,230,138,0.7)] pointer-events-none"
          />
          {back}
        </div>
      );
    }
    return back;
  }

  let rank: Rank;
  let suit: Suit;
  let jewel: Jewel;
  let special: SharedCard['special'] | 'unrevealed';
  let concealed: SharedCard['concealed'];

  if (isStructuredCard(card)) {
    rank = card.rank;
    suit = card.suit;
    jewel = card.jewel;
    // A special not yet turned over (until the 4 corners) pulses just the same.
    special = card.special ?? (card.unrevealedSpecial ? 'unrevealed' : undefined);
    // Prank/Blind/Buried: this attribute of your own card is hidden from you
    // until showdown — the value sent is only a placeholder, so show "?".
    concealed = card.concealed;
  } else {
    ({ rank, suit } = parseNotation(card));
    jewel = Jewel.ONYX; // legacy notation strings carry no jewel — neutral fallback
  }

  const rankArt = RANK_ART[rank];
  const iconArt = ICON_ART[jewel][suit];
  const frameArt = FRAME_ART[jewel];

  return (
    <div className={`relative ${className}`}>
      {special && (
        <div
          data-testid="special-holder-pulse"
          className="special-pulse absolute -inset-[3px] z-10 rounded-[10px] ring-2 ring-amber-200 shadow-[0_0_12px_3px_rgba(253,230,138,0.7)] pointer-events-none"
        />
      )}

      <div
        className={`relative aspect-[5/7] rounded-lg overflow-hidden ${ringClass}`}
        style={{ boxShadow: `0 0 10px 1px ${concealed === 'jewel' ? '#9b9bab' : JEWEL_GLOW[jewel]}55` }}
      >
        {concealed === 'jewel' ? (
          <div className="absolute inset-0 bg-gradient-to-br from-stone-200 to-stone-400 flex items-start justify-center pt-[6%]">
            <span className="text-[10px] font-bold text-stone-600">? jewel</span>
          </div>
        ) : (
          <img
            src={frameArt}
            alt=""
            className="absolute inset-0 w-full h-full object-fill select-none pointer-events-none"
            draggable={false}
          />
        )}

        {concealed === 'suit' ? (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[55%] aspect-square rounded-full bg-black/40 border border-white/30" />
        ) : concealed === 'jewel' ? (
          <div
            className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[3.2em] leading-none ${
              suit === Suit.HEARTS || suit === Suit.DIAMONDS ? 'text-red-700' : 'text-gray-900'
            }`}
          >
            {SUIT_SYMBOL[suit]}
          </div>
        ) : (
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ width: `${ICON_WIDTH[suit] * 100}%` }}
          >
            <img
              src={iconArt}
              alt={`${suit} — ${jewel}`}
              className="w-full h-auto select-none pointer-events-none drop-shadow"
              draggable={false}
            />
          </div>
        )}

        {/* Rank: a single glyph, centered on the icon — never in the
            corners. Painted bright chrome silver with a thin dark edge (the
            glyph's own shape as a mask) so it stands out on every gem, even
            on a small phone card; the image itself only sets the size. */}
        {concealed === 'rank' ? (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[#E0E0E0] text-2xl font-bold font-serif drop-shadow-[0_2px_3px_rgba(0,0,0,0.6)]">
            ?
          </div>
        ) : (
          <div className="rank-silver-edge absolute left-1/2 top-1/2 h-[20%] -translate-x-1/2 -translate-y-1/2">
            <img
              src={rankArt}
              alt={rank}
              className="h-full w-auto select-none pointer-events-none opacity-0"
              draggable={false}
            />
            <div
              aria-hidden
              className="rank-silver absolute inset-0"
              style={{ WebkitMaskImage: `url(${rankArt})`, maskImage: `url(${rankArt})` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayingCard;
